import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { convertLocaleType } from './convertLocaleType';
import { shoppingApiBaseUrl } from './shoppingApiBaseUrl';

type Params = {
  path: string;
  accessToken?: string;
  params?: object;
  localeType?: string;
  lastModified?: string;
  isStaticProps?: boolean;
};

const getAuthorizationHeader = (accessToken: string) => {
  return accessToken != null
    ? {
        Authorization: `Bearer ${accessToken}`
      }
    : {};
};
const getAcceptLanguageHeader = (localeType: string) => {
  return {
    'Accept-Language': convertLocaleType(localeType)
  };
};

const getModifiedSinceHeader = (lastModified: string | undefined) => {
  return lastModified
    ? {
        'If-Modified-Since': lastModified
      }
    : {};
};

type BackendApiFetchResponse<Data> = {
  statusCode: number;
  data: Data;
  lastModified?: string;
  totalPageCount?: string;
};

const BackendApiFetch = async <ResponseType>({
  localeType,
  path,
  accessToken: accessTokenProp,
  lastModified,
  params,
  isStaticProps = false
}: Params): Promise<BackendApiFetchResponse<ResponseType>> => {
  const url = `${shoppingApiBaseUrl(localeType)}${path}`;
  const accessToken = accessTokenProp ?? '';
  const authorizationHeader = getAuthorizationHeader(accessToken);
  const acceptLanguageHeader = localeType ? getAcceptLanguageHeader(localeType) : {};
  const modifiedSinceHeader = getModifiedSinceHeader(lastModified);
  const customHeader = isStaticProps ? { 'X-API-Key': process.env.X_API_KEY } : {};
  const headers = {
    ...authorizationHeader,
    ...acceptLanguageHeader,
    ...modifiedSinceHeader,
    ...customHeader
  } as { Authorization: string };

  return await axios
    .get(url, {
      headers,
      params
    })
    .then((response: AxiosResponse<ResponseType>) => {
      const { data, status, headers: responseHeaders } = response;
      const lastModified: string = responseHeaders['last-modified'];
      const totalPageCount: string = responseHeaders['x-total-page-count'];
      return {
        statusCode: status,
        data,
        lastModified,
        totalPageCount
      };
    })
    .catch((error: AxiosError) => {
      const statusCode = error.response?.status || 500;
      const data = error.response?.data as ResponseType;
      return { statusCode, data };
    });
};

const BackendApiPost = async <ResponseType>(params: Params) => {
  const url = `${shoppingApiBaseUrl(params.localeType)}${params.path}`;
  let data: any = {};
  let statusCode = 200;
  const accessToken = params.accessToken ? params.accessToken : '';
  const authorizationHeader = getAuthorizationHeader(accessToken);
  const acceptLanguageHeader = params.localeType ? getAcceptLanguageHeader(params.localeType) : {};
  const headers = {
    ...authorizationHeader,
    ...acceptLanguageHeader,
    ...acceptLanguageHeader
  } as { Authorization: string };
  await axios
    .post<ResponseType>(url, params.params, {
      headers: headers
    })
    .then((response) => {
      data = response.data;
    })
    .catch((error) => {
      statusCode = 500;
      data = error.response.data;
    });
  return { statusCode, data };
};

const BackendApiDelete = async <ResponseType>(params: Params) => {
  const url = `${shoppingApiBaseUrl(params.localeType)}${params.path}`;
  let data: any = {};
  let statusCode = 200;
  const accessToken = params.accessToken ? params.accessToken : '';
  const authorizationHeader = getAuthorizationHeader(accessToken);
  const acceptLanguageHeader = params.localeType ? getAcceptLanguageHeader(params.localeType) : {};
  const headers = {
    ...authorizationHeader,
    ...acceptLanguageHeader
  } as { Authorization: string };

  await axios
    .delete<ResponseType>(url, {
      headers: headers,
      params: params.params
    })
    .then((response) => {
      data = response.data;
    })
    .catch((error) => {
      statusCode = 500;
      data = error.response.data;
    });
  return { statusCode, data };
};

export { BackendApiDelete, BackendApiFetch, BackendApiPost };
